import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { GitMerge } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function CPDedupe() {
  const [groups, setGroups] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merge, setMerge] = useState({ survivor_id: "", duplicate_id: "", reason: "" });
  const { user } = useAuth();
  const canMerge = ["super_admin","partner"].includes(user?.role);

  const load = () => api.get("/cps/duplicate-scan").then(r => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const doMerge = async () => {
    try { await api.post("/cps/merge", merge); toast.success("Merged"); setMergeOpen(false); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Data Quality</div>
        <h1 className="font-serif-display text-3xl">Duplicate Channel Partners</h1>
        <p className="text-sm text-muted-foreground mt-1">Auto-detected duplicates by RERA / GST / PAN / Mobile. Merge preserves all history.</p>
      </div>
      {groups.length === 0 && <Card className="p-10 text-center text-muted-foreground">No duplicates detected.</Card>}
      {groups.map(g => (
        <Card key={g.key} className="p-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Key: {g.key}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.records.map(cp => (
              <div key={cp.id} className="border border-border rounded p-3 text-sm">
                <div className="font-mono text-[11px] text-muted-foreground">{cp.cp_code}</div>
                <div className="font-medium mt-1">{cp.company_name}</div>
                <div className="text-xs">{cp.contact_person} · {cp.mobile}</div>
                <div className="text-xs text-muted-foreground">{cp.rera_number || "no RERA"} · {cp.gst_number || "no GST"} · {cp.pan || "no PAN"}</div>
                {canMerge && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => { setMerge({ survivor_id: cp.id, duplicate_id: g.records.find(x => x.id !== cp.id)?.id, reason: "" }); setMergeOpen(true); }} data-testid={`merge-btn-${cp.cp_code}`}>
                      <GitMerge size={12} className="mr-1"/> Keep this, merge others
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Merge</DialogTitle></DialogHeader>
          <div className="text-sm">All leads, visits, bookings, interactions and tags from the duplicate CP will be transferred to the survivor. The duplicate will be archived (not deleted).</div>
          <Textarea placeholder="Reason (audit-logged)…" value={merge.reason} onChange={e => setMerge({ ...merge, reason: e.target.value })} data-testid="merge-reason"/>
          <DialogFooter><Button onClick={doMerge} data-testid="merge-confirm">Merge</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
